/*
  The Shipping & returns tab's words, verbatim from screens/order-detail.html and
  scripts/screens/order-detail.js (the Shipping section, the parcel builder and the return panel)
  and scripts/documents.js (the document titles). Where a sentence is ours, or changed, the comment
  beside it says why.
*/

const plural = (n: number, one: string, many: string) =>
    `${n.toLocaleString('en-GB')} ${n === 1 ? one : many}`;

export const SHIPPABLE_COPY =
    'Only products that are Pending, Awaiting payment, Confirmed, Secured or Received can go in a parcel.';

export const SHIPPING = {
    tab: 'Shipping & returns',
    card: {
        title: 'Shipments',
        count: (n: number) => plural(n, 'parcel', 'parcels'),
        add: 'Add parcel',
        empty: {
            title: 'Nothing shipped yet',
            body: "Build a parcel from the order's items. Saving it calls the MyDHL Shipment service - label, commercial invoice, tracking and courier pickup in one go.",
        },
        // The prototype goes on "Delivered parcels move the products into the client's wardrobe in
        // the app." Nothing here moves a line yet (what shipping does to the client app is still to
        // be decided), so that half is left out rather than promised.
        allPacked: 'Every item on this order is packed.',
        nothingToShip: `Nothing left to ship. ${SHIPPABLE_COPY}`,
        // Ours: an environment without DHL (no account yet) reads, and says why nothing ships.
        notSetUp: 'DHL shipping is not set up here, so no label can be created.',
        readOnly: 'A deleted order is kept for the record and ships nothing.',
        loading: 'Loading the shipments…',
        failed: 'The shipments could not be loaded.',
        retryLoad: 'Try again',
    },
    parcel: {
        title: (n: number) => `PARCEL ${n}`,
        meta: (reference: string, service: string, kg: string, items: number) =>
            `${reference} · ${service} · ${kg} KG · ${plural(items, 'ITEM', 'ITEMS')}`.toUpperCase(),
        labels: {
            service: 'Service',
            // Ours, with the item rows' customs line: ALS-31's rules ask for the parcel's from and
            // destination and its commodity codes on the card, which the prototype's card leaves out.
            route: 'Route',
            grossWeight: 'Gross weight',
            dimensions: 'Dimensions',
            tracking: 'Tracking number',
            incoterms: 'Incoterms',
            declared: 'Declared value',
            pickup: 'Courier pickup',
        },
        route: (from: string, to: string) => `${from} → ${to}`,
        customs: (code: string, country: string) => `HS ${code} · Made in ${country}`,
        trackingPending: 'Assigned when the label is created',
        pickup: {
            BookedWithLabel: 'Booked with the label',
            Booked: 'Booked',
            NotBooked: 'Not booked',
        },
        incoterm: { DDP: 'DDP · ALS account', DAP: 'DAP · Receiver' },
        badge: { pending: 'Label pending', exception: 'Exception' },
        steps: ['Label created', 'Picked up', 'In transit', 'Delivered'],
        queued: {
            title: 'Queued for retry',
            body: 'The DHL label service was unavailable when this parcel was saved. The queue retries automatically every 10 minutes, or retry now. Nothing ships until the label exists.',
            // Ours: DHL refused the parcel on a later attempt, so the queue stopped.
            refused: (why: string) =>
                `DHL refused this parcel on the last attempt: ${why} Retry once it is put right, or remove the parcel.`,
            retry: 'Retry label now',
            retrying: 'Calling MyDHL…',
            remove: 'Remove parcel',
        },
        timeline: {
            title: 'Tracking timeline',
            // The prototype adds "· mirrored to the client app"; nothing reaches the app yet.
            subtitle: 'Synced from DHL',
            track: 'Track on DHL',
        },
        scan: {
            awaitingPickup: 'Awaiting pickup',
            pickedUp: (where: string | null) => (where ? `Picked up ${where}` : 'Picked up'),
            inTransit: (where: string | null) =>
                where ? `In transit · last scan ${where}` : 'In transit',
            delivered: (signedBy: string | null) =>
                signedBy ? `Delivered · Signed by ${signedBy}` : 'Delivered',
            estimated: (day: string) => `Est. delivery ${day}`,
            signature: 'Signature required',
            insured: (value: string) => `Insured ${value}`,
            lastScan: (where: string) => `last scan ${where}`,
        },
        // The prototype's "Send customs documents" would need DHL's paperless trade, which nobody
        // has confirmed for the account; staff deal with DHL themselves and record it here.
        clearException: 'Mark as dealt with',
        exceptionCleared: 'Marked as dealt with',
        docs: {
            label: (waybill: string) => `Label ${waybill}.pdf`,
            // Ours: the courier's copy DHL makes beside the label, so the label prints on one sheet.
            waybillCopy: (waybill: string) => `Waybill copy ${waybill}.pdf`,
            invoice: (reference: string) => `Commercial invoice ${reference}.pdf`,
            // Ours: DHL's signed proof of delivery, fetched when first asked for.
            proof: 'Proof of delivery.pdf',
        },
        // Ours: a label DHL made after a lost answer, whose PDF cannot be fetched from DHL again.
        labelLost:
            'DHL created this label after a lost answer, and its PDF cannot be fetched again. Void the parcel and create it again if the label is needed.',
        generateReturn: 'Generate return label',
        void: 'Void label',
    },
    returns: {
        title: 'Return label',
        steps: ['Label created', 'Dropped off', 'In transit', 'Received'],
        retry: 'Retry return label',
        remove: 'Remove',
        track: 'Track return on DHL',
        void: 'Void return label',
        queuedNote:
            'Queued for retry until the DHL API is back. The client has not been sent anything.',
        receivedNote:
            'Received at the studio. Set the line to Return received to raise a credit note.',
        doc: (waybill: string) => `Return label ${waybill}.pdf`,
        // Ours, as on the parcel.
        waybillCopy: (waybill: string) => `Return waybill copy ${waybill}.pdf`,
        covers: (names: string[]) =>
            `Covers ${plural(names.length, 'item', 'items')} · ${names.join(', ')}`,
    },
    confirm: {
        keep: 'Keep it',
        removeParcel: {
            title: 'Remove the queued parcel?',
            body: 'The parcel never got a label, so nothing has shipped. Its items become packable again.',
            confirm: 'Remove parcel',
        },
        voidParcel: {
            title: 'Void this label?',
            body: (waybill: string) =>
                `DHL has not picked this parcel up, so the label ${waybill} can be voided. Its items become packable again.`,
            // Ours (Luka, 6 Oct 2026): what voiding is at DHL, said before anyone does it. The
            // pickup is named only when one was booked (QA, 8 Oct 2026).
            warning: (pickupBooked: boolean) =>
                pickupBooked
                    ? 'DHL cannot cancel a label, only its courier pickup, which this cancels. A label that is never scanned never becomes a shipment, but DHL does not say whether an unused label is charged. Do not hand this label to a courier afterwards.'
                    : 'DHL cannot cancel a label. A label that is never scanned never becomes a shipment, but DHL does not say whether an unused label is charged. Do not hand this label to a courier afterwards.',
            confirm: 'Void label',
        },
        removeReturn: {
            title: 'Remove the queued return?',
            body: 'No label was ever created, so the client has nothing that stops working. The items can be put on a new return whenever DHL is back.',
            confirm: 'Remove',
        },
        voidReturn: {
            title: 'Void this return label?',
            body: (waybill: string) =>
                `Return label ${waybill} has not been scanned, so DHL can void it. The client’s copy stops working and a new one can be generated.`,
            confirm: 'Void return label',
        },
    },
    /** On Generate return label and a return's own Retry, Remove and Void, which stay and say why, for someone whose returns switch is off (ALS-522, ALS-526). */
    returnRefused: 'Returns are switched off on your account. An admin can turn them on in Users.',
    builder: {
        title: 'Add parcel',
        subtitle: 'Saving creates the DHL label, commercial invoice, tracking and pickup together',
        items: 'Items in this parcel',
        held: (count: number, statuses: string[]) =>
            `${plural(count, 'product is', 'products are')} not listed (${statuses.join(', ')}). ${SHIPPABLE_COPY}`,
        commodities: 'Commodity lines (customs)',
        incomplete: 'Incomplete',
        auto: 'Auto',
        customsDescription: 'Customs description',
        hs: 'Commodity code (HS) · library or typed',
        hsPick: 'Type or pick…',
        hsSearch: 'Search the library…',
        hsTyped: (code: string) => `Use ${code}`,
        hsPlaceholder: 'e.g. 6204.43.00',
        quantity: 'Quantity and unit',
        pieces: (n: number) => plural(n, 'piece', 'pieces'),
        netWeight: 'Net weight (kg)',
        netWeightPlaceholder: '0.8',
        // Ours: DHL requires it on every commodity line and 63% of order lines have no origin.
        country: 'Country of manufacture',
        countryPick: 'Choose a country',
        parcel: 'Parcel',
        reference: 'Parcel reference',
        grossWeight: 'Gross weight (kg)',
        grossPlaceholder: '1.2',
        dimensions: 'Dimensions (L × W × H cm)',
        dimensionsPlaceholder: '30 × 24 × 10',
        declared: 'Declared value (insurance)',
        incoterms: 'Incoterms · duties and taxes billed to',
        signature: 'Signature on delivery',
        insurance: 'Insurance at declared value',
        pickup: 'Book courier pickup with the label',
        shipFrom: (from: string) => `Ship from ${from}`,
        cancel: 'Cancel',
        save: (pickup: boolean) => (pickup ? 'Create label & book pickup' : 'Create label'),
        saving: 'Calling MyDHL Shipment…',
        problems: (list: string[]) => `Cannot create the DHL label yet · ${list.join(' · ')}`,
        problem: {
            pick: 'pick at least one item',
            gross: 'gross weight',
            dims: 'dimensions',
            reference: 'parcel reference',
        },
        errors: {
            customsDescription: 'DHL requires the customs description.',
            commodityCode: 'Type a code or pick one from the library.',
            commodityCodeFormat: 'An HS code has 6 to 10 digits.',
            netWeight: 'DHL requires the net weight.',
            country: 'DHL requires the country the item was made in.',
            gross: 'A number above zero, e.g. 1.2',
            dims: 'Length × width × height in cm, e.g. 30 × 24 × 10',
            reference: 'Appears on the DHL business invoice.',
            declared: 'Zero or more.',
        },
    },
    returnBuilder: {
        title: 'Create return label',
        subtitle: 'Linked to the invoice for customs',
        intro: (invoice: string) =>
            `Tick what is coming back. The return label and its customs declaration cover only the selected items, valued at their sale price and linked to invoice ${invoice}.`,
        pickOne: 'Tick at least one item.',
        cancel: 'Cancel',
        save: 'Generate return label',
        saving: 'Calling MyDHL…',
    },
    documents: {
        label: {
            title: 'DHL shipping label',
            subtitle: (reference: string) => `${reference} · generated by the MyDHL API on save`,
        },
        returnLabel: {
            title: 'DHL return label',
            subtitle: (reference: string) => `${reference} · generated by the MyDHL API`,
        },
        // Ours.
        waybillCopy: {
            title: 'DHL waybill copy',
            subtitle: (reference: string) => `${reference} · handed to the courier with the parcel`,
        },
        invoice: {
            title: 'Commercial invoice',
            subtitle: (reference: string) => `${reference} · travels with the parcel`,
        },
        // Ours.
        proof: {
            title: 'Proof of delivery',
            subtitle: (reference: string) => `${reference} · signed for at delivery`,
        },
        download: 'Download PDF',
        downloadFailed: 'That PDF could not be downloaded.',
        print: 'Print',
        printFailed: 'That PDF could not be printed. Download it and print it from there.',
        open: 'Open the PDF',
        /**
         * Ours (ALS-564): an invoice or a credit note holding a name or an address in a script its
         * fonts lack. The document is frozen as it was issued, so renaming the client changes nothing.
         */
        unprintable: (characters: string) =>
            `This PDF cannot be made: its fonts cannot print ${characters}, in a name or an address it holds. Trying again will not help. Ask the team to add a font for them.`,
        // Ours: the viewer fetches the PDF before it draws it (ALS-404), which the prototype's
        // drawn sheets never wait for.
        loading: 'Loading the PDF…',
        failed: 'The PDF could not be shown here.',
    },
    toast: {
        // A parcel that stays in the UK declares nothing, so it has no commercial invoice (QA, 8 Oct 2026).
        created: (waybill: string, pickup: boolean, invoice: boolean) =>
            `${invoice ? 'DHL label and commercial invoice created' : 'DHL label created'}${pickup ? ', courier pickup booked' : ''} · ${waybill}`,
        queued: 'Parcel saved without a label · queued for retry until the DHL API is back',
        retried: (waybill: string, invoice: boolean) =>
            `Retry succeeded · ${invoice ? 'DHL label and commercial invoice created' : 'DHL label created'} · ${waybill}`,
        stillQueued: 'DHL is still unavailable · the label stays queued',
        voided: (waybill: string) => `Label ${waybill} voided · items released`,
        removed: 'Queued parcel removed',
        returnCreated: (waybill: string, items: number) =>
            `Return label created · ${waybill} · ${plural(items, 'item', 'items')}`,
        returnQueued: 'Return saved without a label · queued for retry until the DHL API is back',
        returnRetried: (waybill: string) => `Retry succeeded · return label created · ${waybill}`,
        returnRemoved: 'Queued return removed',
        returnVoided: 'Return label voided',
        failed: 'That did not go through',
    },
    parcelColumn: (n: number) => `P${n}`,
};
